import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { Check, Minus, ArrowRight } from 'lucide-react';

const packages = [
  {
    name: 'Mushin Factory OS Lite',
    badge: 'Best for first rollout',
    desc: 'Affordable, department-level first implementation. Start with core quality execution and IATF-aligned compliance workflows.',
    fit: 'Tier 2 manufacturers, first-time implementers, single-department rollout',
    highlights: ['Up to 10 named users', 'Single department coverage', 'Core quality execution', 'Basic standards management', 'Basic complaint tracking', 'Starter dashboards'],
  },
  {
    name: 'Mushin Factory OS Pro',
    badge: 'Best for plant-wide control',
    desc: 'Comprehensive plant-wide coverage with deeper IATF / VDA 6.3 workflow control, multiple linked departments, and advanced reporting.',
    fit: 'Tier 1 manufacturers, multi-department operations, serious plant execution',
    highlights: ['Up to 50 named users', 'Multiple department coverage', 'Full quality execution', 'Advanced standards management', 'Full CAPA / 8D workflows', 'Advanced dashboards & analytics'],
    featured: true,
  },
  {
    name: 'Mushin Factory OS Enterprise',
    badge: 'Best for advanced governance',
    desc: 'Full plant and multi-plant readiness with advanced governance, custom integrations, and enterprise-grade IATF control.',
    fit: 'OEMs, multi-plant groups, enterprise governance needs',
    highlights: ['Unlimited named users', 'Plant-wide + multi-plant', 'Complete module access', 'Custom integrations', 'Advanced governance', 'Dedicated rollout support'],
  },
];

const comparisonRows = [
  { feature: 'Named Users', lite: 'Up to 10', pro: 'Up to 50', enterprise: 'Unlimited' },
  { feature: 'Department Coverage', lite: 'Single', pro: 'Multiple', enterprise: 'Plant-wide' },
  { feature: 'Forms / Checksheets', lite: true, pro: true, enterprise: true },
  { feature: 'Dashboards', lite: 'Basic', pro: 'Advanced', enterprise: 'Custom' },
  { feature: 'Workflow Approvals', lite: 'Limited', pro: true, enterprise: true },
  { feature: 'Quality Execution', lite: true, pro: true, enterprise: true },
  { feature: 'Standards Management', lite: 'Limited', pro: true, enterprise: true },
  { feature: 'Complaints / CAPA / 8D', lite: 'Basic', pro: true, enterprise: true },
  { feature: 'End-to-End Traceability', lite: 'Basic', pro: true, enterprise: true },
  { feature: 'NPD / APQP / PPAP', lite: false, pro: true, enterprise: true },
  { feature: 'IQC / Incoming Quality', lite: false, pro: true, enterprise: true },
  { feature: 'Production & Maintenance', lite: false, pro: true, enterprise: true },
  { feature: 'Calibration Lab', lite: false, pro: 'Limited', enterprise: true },
  { feature: 'Training / DOJO / Clause 5', lite: false, pro: 'Limited', enterprise: true },
  { feature: 'Stores / FIFO', lite: false, pro: false, enterprise: true },
  { feature: 'Analytics / MIS', lite: 'Basic', pro: 'Advanced', enterprise: 'Custom' },
  { feature: 'Role-based Access', lite: true, pro: true, enterprise: true },
  { feature: 'Audit Support Workflows', lite: 'Basic', pro: true, enterprise: true },
  { feature: 'Integrations', lite: false, pro: 'Limited', enterprise: 'Custom' },
  { feature: 'Multi-plant Readiness', lite: false, pro: false, enterprise: true },
  { feature: 'Advanced Governance', lite: false, pro: false, enterprise: true },
  { feature: 'Dedicated Rollout Support', lite: false, pro: 'Available', enterprise: 'Included' },
];

function CellValue({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-[#C41E3A] mx-auto" />;
  if (value === false) return <Minus className="w-4 h-4 text-[#D1D5DB] mx-auto" />;
  return <span className="text-sm text-[#4B5563]">{value}</span>;
}

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>{children}</section>;
}

export default function PackagesPage() {
  return (
    <div className="pt-16">
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Mushin Plans</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="packages-title">
            Choose your starting point
          </h1>
          <p className="mt-7 text-xl text-[#4B5563] max-w-2xl">
            Affordable entry. Paid deployment. Paid complexity. Every manufacturer has a different starting point.
          </p>
        </div>
      </section>

      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-12">Mushin Factory OS Packages</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div key={pkg.name} className={`relative bg-white border rounded-xl p-8 flex flex-col ${pkg.featured ? 'border-[#C41E3A]/40 ring-1 ring-[#C41E3A]/20 shadow-lg shadow-[#C41E3A]/[0.04]' : 'border-[#E5E7EB] shadow-sm'}`} data-testid={`pkg-card-${pkg.name}`}>
                <span className={`inline-block self-start px-3 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider mb-5 ${pkg.featured ? 'bg-[#C41E3A]/10 text-[#C41E3A] border border-[#C41E3A]/20' : 'bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]'}`}>
                  {pkg.badge}
                </span>
                <h3 className="font-heading text-xl font-bold text-[#111827] mb-3">{pkg.name}</h3>
                <p className="text-[15px] text-[#374151] leading-relaxed mb-4">{pkg.desc}</p>
                <p className="text-sm text-[#6B7280] mb-6">Best fit: {pkg.fit}</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {pkg.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2.5 text-[15px] text-[#374151]">
                      <Check className="w-3.5 h-3.5 text-[#C41E3A] flex-shrink-0" strokeWidth={2} />
                      {h}
                    </li>
                  ))}
                </ul>
                <a href="mailto:sales@mushinlabs.com"
                  className={`w-full inline-flex items-center justify-center py-2.5 rounded-full text-[15px] font-medium transition-colors ${pkg.featured ? 'bg-[#C41E3A] hover:bg-[#A11830] text-white' : 'border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151]'}`}
                  data-testid={`pkg-quote-${i}`}>
                  Get a Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-12">Feature comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="packages-comparison-table">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-4 pr-4 text-[15px] font-medium text-[#6B7280] w-[40%]">Feature</th>
                  <th className="text-center py-4 px-4 text-[15px] font-medium text-[#374151] w-[20%]">Lite</th>
                  <th className="text-center py-4 px-4 text-[15px] font-semibold text-[#111827] w-[20%]">Pro</th>
                  <th className="text-center py-4 px-4 text-[15px] font-medium text-[#374151] w-[20%]">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature} className="border-b border-[#F0F0F0]">
                    <td className="py-3.5 pr-4 text-[15px] text-[#374151]">{row.feature}</td>
                    <td className="py-3 px-4 text-center"><CellValue value={row.lite} /></td>
                    <td className="py-3 px-4 text-center bg-[#C41E3A]/[0.02]"><CellValue value={row.pro} /></td>
                    <td className="py-3 px-4 text-center"><CellValue value={row.enterprise} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl p-8 shadow-sm" data-testid="nexus-pricing">
              <div className="w-2 h-8 rounded-full bg-[#6B7280] mb-5" />
              <h3 className="font-heading text-xl font-bold text-[#111827] mb-3">Mushin Vendor Connect</h3>
              <p className="text-sm text-[#374151] leading-relaxed mb-6">
                Supplier quality and collaboration network. Pricing is tailored based on your requirements.
              </p>
              <ul className="space-y-2 mb-6">
                {['Supplier count', 'Workflow depth', 'Compliance complexity', 'Rollout scope'].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-[#4B5563]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6B7280]" />
                    Depends on {item.toLowerCase()}
                  </li>
                ))}
              </ul>
              <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-5 py-2.5 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="nexus-pricing-quote">
                Get a Quote <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl p-8 shadow-sm" data-testid="connect-pricing">
              <div className="w-2 h-8 rounded-full bg-[#374151] mb-5" />
              <h3 className="font-heading text-xl font-bold text-[#111827] mb-3">IoT & Machine Data</h3>
              <p className="text-sm text-[#374151] leading-relaxed mb-6">
                Machine and process data layer. Pricing is tailored based on your requirements.
              </p>
              <ul className="space-y-2 mb-6">
                {['Machine count', 'Line count', 'Data depth', 'Workflow linkage'].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-[#4B5563]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#374151]" />
                    Depends on {item.toLowerCase()}
                  </li>
                ))}
              </ul>
              <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-5 py-2.5 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="connect-pricing-quote">
                Get a Quote <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </Section>

      <section className="py-24 text-center border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-[#111827]">Ready to get started?</h2>
          <p className="mt-4 text-[#374151]">Our team will help you choose the right package and plan your rollout.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="packages-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="packages-quote-cta">Get a Quote</a>
          </div>
        </div>
      </section>
    </div>
  );
}
